<!-- src/views/profile/ProfileView.vue -->
<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <!-- Top bar -->
        <div class="page-bar py-3">
          <div class="d-flex align-items-center">
            <BackButton class="btn btn-link p-0 mr-2 ml-2" fallback="/host">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">
              {{ 'Profile' }}
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-8 mt-3">
        <!-- Profile card -->
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center">
              <div class="mr-3 mb-3 mb-md-0">
                <img
                  :src="avatarSrc(profile?.name, profile?.photo)"
                  class="rounded-circle"
                  style="height:80px;width:80px;object-fit:cover;"
                  alt="avatar"
                />
              </div>
              <div class="flex-grow-1">
                <div class="">
                  <h5 class="mb-0 mr-2" style="text-transform: capitalize;">{{ profile?.name || '—' }}</h5>
                  <p class="mb-1 small">@{{ (profile?.username || slug) || '' }}</p>
                </div>
                <div class="text-muted small">
                  <span class="bg-primary p-1 px-2 rounded text-white">
                    <i class="fas fa-envelope mr-1"></i>
                    <span>{{ profile?.email || 'not provided' }}</span>
                  </span>
                </div>
              </div>
              <div v-if="loadingProfile" class="ml-auto">
                <div class="spinner-border spinner-border-sm text-primary"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-8">
         <div class="card">
           <div class="card-body">

                    <!-- Skeleton Loaders -->
                    <div v-if="loading" class="skeleton-container">
                      <div v-for="section in 3" :key="`section-${section}`" class="skeleton-section">
                        <div class="skeleton-title"></div>
                        <div class="skeleton-cards">
                          <div
                            v-for="card in 2"
                            :key="`card-${section}-${card}`"
                            class="skeleton-card"
                          >
                            <div class="skeleton-image"></div>
                            <div class="skeleton-content">
                              <div class="skeleton-event-title"></div>
                              <div class="skeleton-text"></div>
                              <div class="skeleton-text short"></div>
                              <div class="skeleton-meta">
                                <div class="skeleton-badge"></div>
                                <div class="skeleton-badge"></div>
                              </div>
                              <div class="skeleton-actions">
                                <div class="skeleton-button"></div>
                                <div class="skeleton-button"></div>
                                <div class="skeleton-button"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Actual Content -->
                    <template v-else>
                      <InvitationList
                        title="Active Invitations"
                        :items="active"
                        @rsvp-updated="loadInvitations"
                      />
                      <InvitationList
                        title="Upcoming Invitations"
                        :items="upcoming"
                        @rsvp-updated="loadInvitations"
                      />
                      <InvitationList
                        title="Past Invitations"
                        :items="past"
                        @rsvp-updated="loadInvitations"
                      />
                      <InvitationList
                        title="Draft Invitations"
                        :items="draft"
                        @rsvp-updated="loadInvitations"
                      />
                    </template>


           </div>
         </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !active.length && !upcoming.length && !past.length && !draft.length" class="row mt-4">
      <div class="col-12 text-center py-5">
        <img
          src="/assets/placeholders/no-invitations.png"
          alt="No invitations"
          style="max-width: 240px;"
          class="mb-3"
          @error="handleImageError"
        />
        <p class="text-muted mb-3">No invitations received yet</p>
        <p class="text-muted small">When someone sends you an invitation, it will appear here</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useHead } from "@vueuse/head";
import { useSessionStore } from "@/stores/session";
import api from "@/services/api";
import InvitationList from '@/components/common/ReceivedInvitationList.vue';
import BackButton from '@/components/common/BackButton.vue';

export default {
  name: "ProfileView",
  components: { InvitationList, BackButton },
  setup() {
    useHead({
      title: "Profile - Invi8",
      meta: [
        { name: "description", content: "User profile and received invitations." },
        { name: "keywords", content: "profile, invites, photos, events" },
      ],
    });

    const session = useSessionStore();
    const route = useRoute();

    const slug = ref(
      route.params.slug || route.params.id || route.params.username || route.params.profile_id || ""
    );

    const loggedInId = ref(session.userId || "");

    const profile = ref(null);
    const loadingProfile = ref(false);

    // Invitations lists
    const active = ref([]);
    const upcoming = ref([]);
    const past = ref([]);
    const draft = ref([]);

    const loading = ref(true);
    const loadingTimeout = ref(null);

    const page = ref(1);
    const limit = ref(12);
    const hasMore = ref(false);
    const loadingMore = ref(false);

    const q = ref("");

    // computed filtered list if needed (keeps invites variable for legacy)
    const invites = computed(() => [...active.value, ...upcoming.value, ...past.value, ...draft.value]);

    const filtered = computed(() => {
      const term = q.value.trim().toLowerCase();
      if (!term) return invites.value;
      return invites.value.filter((i) => {
        const hay = `${i.orgname || ""} ${i.hostname || ""} ${i.address || ""} ${i.location || ""}`.toLowerCase();
        return hay.includes(term);
      });
    });

    const avatarSrc = (name, photo) => {
      if (photo) return photo;
      const txt = ((name || slug.value || "?").substr(0, 1) || "?").toUpperCase();
      return `https://dummyimage.com/200x200/152238/ffffff.png&text=${encodeURIComponent(txt)}`;
    };

    const getCover = (inv) =>
      inv.p1_url ||
      inv.photo1 ||
      inv.banner ||
      "https://dummyimage.com/800x450/e9edf5/7c8697.png&text=Event+Cover";

    const prettyDate = (d, t) => {
      if (!d) return "—";
      try {
        const parts = String(d).split("-").map((n) => parseInt(n, 10));
        const y = parts[0];
        const m = parts[1] || 1;
        const day = parts[2] || 1;
        const dt = new Date(y, m - 1, day);
        const ds = dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
        return t ? `${ds} • ${t}` : ds;
      } catch {
        return t ? `${d} • ${t}` : d;
      }
    };

    const statusClass = (s) => {
      const v = String(s || "").toLowerCase();
      if (v.includes("going") || v === "accepted") return "badge-success";
      if (v.includes("maybe") || v.includes("pending")) return "badge-warning";
      if (v.includes("declined") || v.includes("not")) return "badge-danger";
      return "badge-info";
    };

    // Fetch profile (used on top)
    const fetchProfile = async () => {
      loadingProfile.value = true;
      try {
        const fd = new FormData();
        fd.append("id", slug.value);
        fd.append("user_id", loggedInId.value || "");
        const res = await api.getProfileApi(fd);
        const data = res?.data;
        profile.value =
          data?.profile ||
          (Array.isArray(data?.data) ? data.data[0] : data?.data) ||
          data ||
          null;
      } catch (e) {
        console.error("profile error", e);
        profile.value = null;
      } finally {
        loadingProfile.value = false;
      }
    };

    // Load invitations - robust to different API shapes
    const loadInvitations = async (opts = { page: 1, limit: 12 }) => {
      loading.value = true;

      // minimum UX delay so skeleton is visible briefly
      if (loadingTimeout.value) clearTimeout(loadingTimeout.value);
      loadingTimeout.value = setTimeout(() => {}, 300);

      try {
        const formData = new FormData();
        formData.append("profile_id", slug.value);
        formData.append("id", loggedInId.value || "");
        formData.append("page", opts.page || 1);
        formData.append("limit", opts.limit || limit.value);

        const response = await api.getReceivedInvitationsProfile(formData);
        const payload = response?.data ?? response ?? {};

        // Support both { status: 'ok', list: [...] } and raw arrays/objects
        const listActive = payload.list || payload.data || payload.items || [];
        const listUpcoming = payload.list_upcoming || payload.upcoming || [];
        const listPast = payload.list_old || payload.past || [];
        const listDraft = payload.list_draft || payload.draft || [];

        // Coerce to arrays
        active.value = Array.isArray(listActive) ? listActive : [];
        upcoming.value = Array.isArray(listUpcoming) ? listUpcoming : [];
        past.value = Array.isArray(listPast) ? listPast : [];
        draft.value = Array.isArray(listDraft) ? listDraft : [];

        // pager (if api provides)
        hasMore.value = !!(payload.has_more || payload.next_page || ( (opts.page * opts.limit) < (payload.total || 0) ));
      } catch (err) {
        console.error('Error loading received invitations:', err);
        active.value = [];
        upcoming.value = [];
        past.value = [];
        draft.value = [];
      } finally {
        loading.value = false;
        if (loadingTimeout.value) {
          clearTimeout(loadingTimeout.value);
          loadingTimeout.value = null;
        }
      }
    };

    // Load more (simple pager stub)
    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return;
      loadingMore.value = true;
      page.value += 1;
      await loadInvitations({ page: page.value, limit: limit.value });
      loadingMore.value = false;
    };

    // simple search apply
    const applySearch = (term) => {
      q.value = term || "";
    };

    // safe image onerror handler (replace broken cover)
    const handleImageError = (e) => {
      e.target.src = "/assets/placeholders/no-invitations.png";
    };

    // on mount: load profile & invites
    onMounted(async () => {
      await Promise.all([fetchProfile(), loadInvitations()]);
    });

    onBeforeUnmount(() => {
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value);
        loadingTimeout.value = null;
      }
    });

    return {
      slug,
      profile,
      loadingProfile,
      // invitation lists
      active,
      upcoming,
      past,
      draft,
      loading,
      page,
      limit,
      hasMore,
      loadingMore,
      q,
      filtered,
      avatarSrc,
      getCover,
      prettyDate,
      statusClass,
      fetchProfile,
      loadInvitations,
      loadMore,
      applySearch,
      handleImageError,
    };
  },
};
</script>

<style scoped>
.invite-card {
  border: 1px solid #eef1f5;
  transition: box-shadow 0.15s ease;
}
.invite-card:hover {
  box-shadow: 0 10px 24px rgba(21, 34, 56, 0.08);
}
.status-pill {
  position: absolute;
  left: 10px;
  bottom: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(21, 34, 56, 0.15);
}

/* Minimal skeleton styles — replace with your project's skeleton CSS if any */
.skeleton-container { padding: 8px 0; }
.skeleton-section { margin-bottom: 16px; }
.skeleton-title { height: 16px; background: #eef2f7; margin-bottom: 12px; width: 30%; border-radius: 4px; }
.skeleton-cards { display:flex; gap:12px; }
.skeleton-card { display:flex; gap:12px; background:#fff; padding:12px; border-radius:8px; border:1px solid #f1f4f8; flex:1; }
.skeleton-image { width:120px; height:80px; background:#eef2f7; border-radius:6px; }
.skeleton-content { flex:1; }
.skeleton-event-title { width:60%; height:14px; background:#eef2f7; margin-bottom:8px; border-radius:4px; }
.skeleton-text { width:100%; height:10px; background:#f3f6fa; margin-bottom:6px; border-radius:4px; }
.skeleton-text.short { width:45%; }
.skeleton-meta { display:flex; gap:8px; margin:8px 0; }
.skeleton-badge { width:60px; height:20px; background:#eef2f7; border-radius:12px; }
.skeleton-actions { display:flex; gap:8px; margin-top:8px; }
.skeleton-button { width:60px; height:28px; background:#eef2f7; border-radius:6px; }
</style>
